<template>
    <v-layout row wrap>
        <v-flex xs12 md7 lg6>
            <v-layout row wrap v-if="previewVideo && previewVideo.title">
                <v-flex xs12>
                    <h3 class="grey--text text--darken-3 pb-3">{{ $t('screener.quick.event.configuration.step.form.selected.video') }}</h3>
                </v-flex>
                <v-flex xs12>
                    <v-card flat class="pa-1 selected-video-card">
                        <v-layout row wrap align-center>
                            <v-flex xs2>
                                <template v-if="isUploadedVideoProcessing">
                                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                                </template>
                                <template v-else>
                                    <template v-if="('thumbnailUrl' in previewVideo) && previewVideo.thumbnailUrl">
                                        <img contain :src="previewVideo.thumbnailUrl" :style="{borderRadius:'8px', height: '100%', objectFit: 'cover', width: '100%'}" />
                                    </template>
                                </template>
                            </v-flex>
                            <v-flex xs7>
                                <v-layout row wrap class="px-1">
                                    <v-flex xs12>
                                        <v-layout row wrap class="px-1">
                                            <v-flex xs9 md8>
                                                <h4 class="grey--text text--darken-1">{{ previewVideo.title }}</h4>
                                                <div v-if="previewVideo.fileSize" class="caption grey--text font-weight-bold">Size: {{ previewVideo.fileSize }} MB</div>
                                            </v-flex>
                                        </v-layout>
                                    </v-flex>
                                </v-layout>
                            </v-flex>
                            <v-flex xs3>
                                <v-btn @click="$emit('changeVideoSource')" class="change-video-btn" outline small color="primary">{{ $t('screener.quick.event.creation.step.form.video.upload.change.video.btn') }}</v-btn>
                            </v-flex>
                        </v-layout>
                    </v-card>
                </v-flex>
            </v-layout>
            <!-- Monetization -->
            <v-layout row wrap :class="(previewVideo && previewVideo.title) ? 'mt-5' : null ">
                <v-flex>
                    <h3 class="grey--text text--darken-3">{{ $t('screener.monetization.title') }}</h3>
                    <p class="subheading grey--text text--darken-1 mb-0">{{ $t('screener.quick.event.configuration.step.monetization.desc') }}</p>
                </v-flex>
            </v-layout>
            <v-layout row wrap>
                <v-flex xs12>
                    <v-radio-group v-model="formData.monetization_enabled" row single-line hide-details>
                        <v-radio label="Free" :value="false" color="primary"></v-radio>
                        <v-radio label="Paid" :value="true" color="primary"></v-radio>
                    </v-radio-group>
                </v-flex>
                <v-flex xs12 v-if="formData.monetization_enabled">
                    <!-- Price Input -->
                    <v-text-field
                        class="mt-3"
                        v-model="formData.monetization[DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE][0].price"
                        :prefix="getCurrencySymbol(formData.monetization[DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE][0].currency)"
                        :label="$t('screener.save.monetization.price')"
                        :rules="formData.monetization_enabled ? [rules.required, rules.floating_number, rules.greaterThanZero]: []"
                        placeholder="0.00"
                        :suffix="formData.monetization[DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE][0].currency"
                        :hint="$t('screener.monetization.paid_screening.edit_warning')"
                        persistent-hint>
                        <template v-slot:append>
                            <v-tooltip top color="white">
                                <template v-slot:activator="{on}">
                                    <v-icon v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <div class="grey--text text--darken-3 body-1" v-html="$t('screener.save.monetization.currency.tip')"></div>
                            </v-tooltip>
                        </template>
                    </v-text-field>
                </v-flex>
            </v-layout>
            <!-- Monetization -->
            <!-- GEO Blocking -->
            <v-layout row wrap class="mt-4">
                <v-flex>
                    <h3 class="grey--text text--darken-3">{{ $t('screener.quick.event.creation.step.form.geo.restriction.text') }}</h3>
                </v-flex>
            </v-layout>
            <v-layout row wrap>
                <v-flex class="">
                    <GeoBlocking v-model="formData.geo_blocking"></GeoBlocking>
                </v-flex>
            </v-layout>
            <v-divider></v-divider>
            <!-- GEO Blocking -->
            <!-- Player Configuration -->
            <v-layout row wrap class="mt-5">
                <v-flex>
                    <h3 class="grey--text text--darken-3">{{ $t('screener.quick.event.creation.step.form.player.configuration') }}</h3>
                </v-flex>
            </v-layout>
            <v-layout row wrap>
                <v-flex xs12>
                    <v-checkbox
                        class="mt-0"
                        :disabled="!videoSrcUrl"
                        v-model="formData.event_configuration.auto_play"
                        color="primary"
                        :label="$t('screener.quick.event.creation.step.form.player.configuration.option.allow_auto_play')"
                    >
                    <template v-slot:append>
                        <v-tooltip top color="white">
                            <template v-slot:activator="{on}">
                                <v-icon v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <div
                                class="grey--text text--darken-3 body-1"
                            >{{ $t('screener.save.form.auto_play.tooltip') }}</div>
                        </v-tooltip>
                    </template>
                    </v-checkbox>
                    <v-checkbox
                        class="mt-0"
                        color="primary"
                        :disabled="!videoSrcUrl"
                        :label="$t('screener.quick.event.creation.step.form.player.configuration.option.display_seeking')"
                        v-model="formData.event_configuration.no_skip"
                    >
                        <template v-slot:append>
                            <v-tooltip top color="white">
                            <template v-slot:activator="{on}">
                                <v-icon v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <div
                                class="grey--text text--darken-3 body-1"
                            >{{ $t('screener.save.form.no_skip.tooltip') }}</div>
                            </v-tooltip>
                        </template>
                    </v-checkbox>
                    <v-checkbox
                        class="mt-0"
                        :disabled="!videoSrcUrl"
                        v-model="formData.event_configuration.no_pause"
                        color="primary"
                        :label="$t('screener.quick.event.creation.step.form.player.configuration.option.disable_play')"
                    >
                        <template v-slot:append>
                            <v-tooltip top color="white">
                            <template v-slot:activator="{on}">
                                <v-icon v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <div
                                class="grey--text text--darken-3 body-1"
                            >{{ $t('screener.save.form.no_pause.tooltip') }}</div>
                            </v-tooltip>
                        </template>
                    </v-checkbox>
                    <v-checkbox
                        class="mt-0"
                        color="primary"
                        v-model="formData.event_configuration.is_full_screen_mode"
                        :label="$t('screener.quick.event.creation.step.form.player.configuration.option.full_screen')"
                    >
                        <template v-slot:append>
                            <v-tooltip top color="white">
                            <template v-slot:activator="{on}">
                                <v-icon v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <div
                                class="grey--text text--darken-3 body-1"
                            >{{ $t('screener.save.form.full_screen_mode.tooltip') }}</div>
                            </v-tooltip>
                        </template>
                    </v-checkbox>
                </v-flex>
            </v-layout>
            <!-- Player Configuration -->
        </v-flex>
        <v-flex xs12 md5 lg6 class="pl-4">
            <v-layout column justify-space-between fill-height>
                <v-layout row wrap>
                    <v-flex sx12>
                        <template v-if="uploadingVideoStatus && uploadingVideoStatus == 'in_progress'">
                            <div >
                                <v-card class="video-uploader grey d-flex align-center justify-center" height="150px">
                                    <div class="text-xs-center">
                                        <div>
                                            <span class="white--text font-weight-bold text-capitalize">Preparing...</span>
                                        </div>
                                    </div>
                                </v-card>
                                <v-progress-linear value="100"></v-progress-linear>
                            </div>
                            <v-flex sx12>
                                <h4>Video Details</h4>
                            </v-flex>
                            <v-flex xs12>
                                <v-card flat class="pa-1 selected-video-card">
                                    <v-layout row wrap align-center>
                                        <v-flex xs9>
                                            <v-layout row wrap class="px-1">
                                                <v-flex xs11 md11>
                                                    <h4 class="grey--text text--darken-1">{{ videoFileName }}</h4>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                        <v-flex xs3>
                                            <v-btn class="change-video-btn" outline small color="primary" @click="reloadUploadStatus">
                                                <v-icon v-on="on">mdi-reload</v-icon>
                                            </v-btn>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </v-flex>
                        </template>
                        <template v-else-if="uploadingVideoStatus && uploadingVideoStatus == 'uploading'">
                            <template v-if="uploadingVideo">
                                <template v-if="('isUploading' in uploadingVideo) && uploadingVideo.isUploading">
                                    <v-card class="video-uploader grey d-flex align-center justify-center" height="150px" @click="openFilePicker('video_movie')">
                                        <div class="text-xs-center">
                                            <div v-if="uploadingVideo.isUploading">
                                                <span class="white--text font-weight-bold text-capitalize">{{ uploadingVideo.status}} ({{ uploadingVideo.progress }}%)</span>
                                            </div>
                                        </div>
                                    </v-card>
                                </template>
                                <template v-if="('progress' in uploadingVideo) && uploadingVideo.progress">
                                    <v-progress-linear :value="uploadingVideo.progress"></v-progress-linear>
                                </template>
                            </template>
                        </template>
                        <template v-else-if="previewVideo && previewVideo.hlsUrl">
                            <template v-if="previewVideo && Object.keys(previewVideo).length > 0 && Object.values(previewVideo).some(value => value !== null && value !== '')">
                                <ScreenerPlayer
                                    :movie="previewVideo.hlsUrl"
                                    :subtitles="subtitles"
                                    v-if="currentStep == 2"
                                />
                                <h4 class="grey--text text--darken-3">{{ previewVideo.title }}</h4>
                                <div v-if="previewVideo.fileSize" class="caption grey--text font-weight-bold">Size: {{ previewVideo.fileSize }}</div>
                            </template>
                            <template v-else-if="draftScreener && draftScreener.films[0] && draftScreener.films[0].external_url">
                                <ScreenerPlayer :movie="draftScreener.films[0].external_url" v-if="currentStep == 2"/>
                                <template v-if="videoFileName">
                                    <v-flex sx12>
                                        <h2>Video Details</h2>
                                    </v-flex>
                                    <v-flex xs12>
                                        <v-card flat class="pa-1 selected-video-card">
                                            <v-layout row wrap align-center>
                                                <v-flex xs12>
                                                    <v-layout row wrap class="px-1">
                                                        <v-flex xs11 md11>
                                                            <h4 class="grey--text text--darken-3">{{ videoFileName }}</h4>
                                                        </v-flex>
                                                    </v-layout>
                                                </v-flex>
                                            </v-layout>
                                        </v-card>
                                    </v-flex>
                                </template>
                            </template>
                        </template>
                        <template v-else>
                            <v-card class="video-uploader grey d-flex align-center justify-center" height="150px" @click="openFilePicker('video_movie')">
                                <div class="text-xs-center">
                                    <div>
                                        <span class="white--text font-weight-bold">{{$t('screener.quick.event.configuration.step.form.video.preview.not.available')}}</span>
                                    </div>
                                </div>
                            </v-card>
                        </template>
                    </v-flex>
                </v-layout>
                <!-- Video Thumbnail Part -->
                <v-layout row wrap>
                    <v-flex>
                        <h3 class="grey--text text--darken-3">{{ $t('film.thumbnail.title') }}</h3>
                        <v-layout row wrap class="mb-2" v-if="film" align-content-end>
                            <v-flex xs5>
                                <v-card hover class="video-uploader d-flex align-center justify-center"
                                    height="100px" @click="showDropzoneThumbnailDialog=true">
                                    <div class="text-xs-center">
                                        <v-icon large color="primary">mdi-plus</v-icon>
                                    </div>
                                </v-card>
                                <SingleFileUploader
                                    key="upload_thumbnail"
                                    v-model="showDropzoneThumbnailDialog"
                                    :title="$t('screener.quick.event.film.thumnail')"
                                    :warning="$t('screener.admin.drag_and_drop.warning')"
                                    :attribute="'logo_url'"
                                    :collection="this.DEFINES.COLLECTION_PUBLIC"
                                    :dropzoneOptions="dropzoneOptions('uploadFilmThumbnail')"
                                    @setThumbnal="setThumbnail"
                                    @onError="showError"
                                    @successUpload="successUpload"
                                ></SingleFileUploader>
                            </v-flex>
                            &nbsp;&nbsp;
                            <v-flex xs5 v-if="(film && film.thumburls) || (uploadingVideo && uploadingVideo.thumbnailUrl)">
                                <!-- Media preview -->
                                <v-card
                                    height="100px"
                                    class="text"
                                >
                                <v-layout row wrap>
                                    <v-flex xs12 class="white--text grey lighten-3">
                                        <v-layout row>
                                            <v-flex xs12>
                                                <v-img slot="activator" :src="(film && film.thumburls) || (uploadingVideo && uploadingVideo.thumbnailUrl)" height="100px" contain>
                                                </v-img>
                                            </v-flex>
                                        </v-layout>
                                    </v-flex>
                                </v-layout>
                                </v-card>
                                <!-- <v-card width="170px" class="video-uploader d-flex align-center justify-center mx-2">
                                    <div class="pa-1">
                                        <v-img width="100%" contain :src="(film && film.thumburls) || (uploadingVideo && uploadingVideo.thumbnailUrl)"></v-img>
                                    </div>
                                </v-card> -->
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
                <!-- Video Thumbnail Part -->
            </v-layout>
        </v-flex>
    </v-layout>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import DEFINES from '@/defines';
import rules from '@/validation';
import { currencySymbol, getPricingListFromApi } from '@/mixins/monetization';
import { getCurrencySymbol } from "@/helper";
import GeoBlocking from "@/components/screener/GeoBlocking";
import ScreenerPlayer from "@/components/screener/Player";
import SingleFileUploader from "@/components/media/SingleFileUploader";
import { buildHeaders } from "@/api/_manager";

export default {
    name: 'EventConfiguration',
    components: {
        ScreenerPlayer,
        GeoBlocking,
        SingleFileUploader
    },
    computed: {
        ...mapGetters("company", {
            selected_company_id: "getSelected",
        }),
        previewVideo() {
            // if(this.videoSource == this.DEFINES.QUICK_EVENT.VIDEO_SOURCE.SAMPLE){
            //     return this.DEFINES.QUICK_EVENT.SAMPLE_VIDEO.VIMEO
            // }
            // if(this.videoSource == this.DEFINES.QUICK_EVENT.VIDEO_SOURCE.UPLOAD){
            //     return {
            //         title: (this.uploadingVideo) ? this.uploadingVideo.fileName : null ,
            //         fileSize: (this.uploadingVideo) ? this.uploadingVideo.fileSize : null ,
            //         hlsUrl: (this.uploadingVideo) ? this.uploadingVideo.hlsUrl : null ,
            //         thumbnailUrl: (this.uploadingVideo) ? this.uploadingVideo.thumbnailUrl : null
            //     }
            // }

            return {
                title: this.videoFileName || null,
                fileSize: (this.uploadingVideo) ? this.uploadingVideo.fileSize : null ,
                hlsUrl: this.videoSrcUrl || null ,
                thumbnailUrl: (this.uploadingVideo) ? this.uploadingVideo.thumbnailUrl : null
            }
        },
        isUploadedVideoProcessing() {
            return this.videoSource === this.DEFINES.QUICK_EVENT.VIDEO_SOURCE.UPLOAD &&
                    (this.uploadingVideo && this.uploadingVideo.status !== 'complete');
        },
    },
    props: {
        currentStep: Number,
        uploadingVideoStatus:String,
        videoFileName: String,
        videoSource: {
            type: String,
            default: null
        },
        uploadingVideo: {
            type: Object,
            default: null
        },
        screener:{
            type: Object,
            default: null
        },
        film:{
            type: Object,
            default: null
        },
        draftScreener: {
            type: Object,
            default: null
        },
        videoSrcUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            rules,
            formData: {
                geo_blocking: {
                    blocking_for: null,
                    city: null,
                    blocking_type: null,
                    country_code: []
                },
                event_configuration: {
                    auto_play: true,
                    no_skip: false,
                    no_pause: false,
                    movie_switching_enabled: true,
                    is_full_screen_mode: false
                },
                monetization_enabled:false,
                monetization: this.initializeMonetization(),
                auto_publish: false,
                quick_event_configuration: {}
            },
            showDropzoneThumbnailDialog:false
        }
    },
    mixins: [currencySymbol, getPricingListFromApi],
    methods: {
        dropzoneOptions(uploading_relative_path) {
            return {
                acceptedFiles: "image/*",
                url: `${this.DEFINES.BASE_API_URL}/member/company/${this.selected_company_id}/film/${uploading_relative_path}/${this.film.id}`,
                thumbnailWidth: 200,
                maxFilesize: 1, //MB
                chunking: true,
                chunkSize: 2000000, // Bytes
                destroyDropzone: false,
                headers: buildHeaders().headers,
            };
        },
        initializeMonetization() {
            let SINGLE_PURCHASE_KEY =
                DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE;
            var GROUP_PURCHASE_KEY = DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE;
            let VOD_PURCHASE_KEY = DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE;

            let pricing_plans = {};
            pricing_plans[SINGLE_PURCHASE_KEY] = [
                {
                type: DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE,
                price: null,
                currency: this.$store.state.company.company.currency,
                screening_ids: this.screener?[this.screener.id]:[],
                },
            ];
            pricing_plans[VOD_PURCHASE_KEY] = [
                {
                type: DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE,
                price: null,
                currency: this.$store.state.company.company.currency,
                screening_ids: [],
                },
            ];
            pricing_plans[GROUP_PURCHASE_KEY] = [];
            return pricing_plans;
        },
        getCurrencySymbol() {
            return getCurrencySymbol(this.currency);
        },
        async reloadUploadStatus() {
            await this.$store.dispatch("screener/getDraftScreener", {
                id: this.$route.params.screener_id,
            }).then((res) => {
                if (res) {
                    this.$emit('getDraftScreener',res)
                }
            })
        },
        setThumbnail(event) {
            // console.log(event);
        },
        successUpload(response) {
            this.$emit('updateFilm',response.data.film)
        },
        async showError(event) {
            try {
                await this.$store.dispatch("app/showMessage", {
                message: event.message,
                color: "error",
                });
            } catch (error) {
                this.$store.dispatch("app/showMessage", {
                message: error.message,
                color: "error",
                });
            }
        },
    }
}
</script>
